/**
 * Email HTML structure checks for Master Template save.
 * Guards against silent CKEditor/DOM mutations that hide content
 * (e.g. unclosed Litmus preview-text / preheader div).
 */

const PREVIEW_OPEN_RE =
  /<div\b[^>]*class\s*=\s*["'][^"']*(?:preview-text|litmus-builder-preview-text)[^"']*["'][^>]*>/i;

const DIV_TAG_RE = /<\/?div\b[^>]*>/gi;

const stripNonContentBlocks = (html) =>
  String(html || "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<script\b[\s\S]*?<\/script>/gi, "")
    .replace(/<style\b[\s\S]*?<\/style>/gi, "");

export const hasVisibleEmailHtmlContent = (htmlString) => {
  if (!htmlString || typeof htmlString !== "string") {
    return false;
  }
  // Only strip real HTML tags (not EJS <%= %> or similar), then check for text,
  // images, or template placeholders that will render at send time.
  const textOnly = htmlString
    .replace(/<\/?[a-zA-Z][^>]*>/g, "")
    .replace(/\s+/g, "");
  const hasPlaceholders = /<%[=-][\s\S]*?%>|\{\{[\s\S]*?\}\}/.test(htmlString);
  return (
    textOnly.length > 0 ||
    hasPlaceholders ||
    htmlString.toLowerCase().includes("<img")
  );
};

/**
 * Walk div tags and return whether depth stays balanced.
 * Returns { balanced, minDepth } — minDepth < 0 means an extra closing tag.
 */
const analyzeDivBalance = (html) => {
  const stripped = stripNonContentBlocks(html);
  let depth = 0;
  let minDepth = 0;
  DIV_TAG_RE.lastIndex = 0;
  let match = DIV_TAG_RE.exec(stripped);
  while (match) {
    const tag = match[0];
    if (/^<\//.test(tag)) {
      depth -= 1;
      if (depth < minDepth) {
        minDepth = depth;
      }
    } else if (!/\/\s*>$/.test(tag)) {
      depth += 1;
    }
    match = DIV_TAG_RE.exec(stripped);
  }
  return { balanced: depth === 0 && minDepth >= 0, depth, minDepth };
};

/**
 * True when Litmus/preheader div never closes, or still contains main email
 * content (tables / aria-label email wrapper) — which renders as blank.
 */
export const isEmailContentHiddenInPreheader = (html) => {
  const stripped = stripNonContentBlocks(html);
  const previewOpen = PREVIEW_OPEN_RE.exec(stripped);
  if (!previewOpen) {
    return false;
  }

  const startIdx = previewOpen.index + previewOpen[0].length;
  let depth = 1;
  DIV_TAG_RE.lastIndex = startIdx;
  let closeIdx = -1;
  let match = DIV_TAG_RE.exec(stripped);
  while (match) {
    const tag = match[0];
    if (/^<\//.test(tag)) {
      depth -= 1;
      if (depth === 0) {
        closeIdx = match.index;
        break;
      }
    } else if (!/\/\s*>$/.test(tag)) {
      depth += 1;
    }
    match = DIV_TAG_RE.exec(stripped);
  }

  if (closeIdx === -1) {
    return true;
  }

  const inside = stripped.slice(startIdx, closeIdx);
  return (
    /aria-label\s*=\s*["'][^"']*email/i.test(inside) ||
    /<table\b/i.test(inside)
  );
};

/**
 * @param {string} htmlString
 * @returns {{ invalid: boolean, message?: string }}
 */
export const validateEmailHtmlTemplate = (htmlString) => {
  if (!hasVisibleEmailHtmlContent(htmlString)) {
    return {
      invalid: true,
      message:
        "EMAIL template error! Template HTML has no visible content. " +
        "Save was blocked to prevent blank emails.",
    };
  }

  if (isEmailContentHiddenInPreheader(htmlString)) {
    return {
      invalid: true,
      message:
        "EMAIL template error! Hidden preheader (preview-text) is missing " +
        "a closing </div> or wraps the email body. Fix the HTML before " +
        "saving to prevent blank emails.",
    };
  }

  const { balanced } = analyzeDivBalance(htmlString);
  if (!balanced) {
    return {
      invalid: true,
      message:
        "EMAIL template error! Unbalanced <div> tags detected. Fix the " +
        "HTML before saving to prevent blank or broken emails.",
    };
  }

  return { invalid: false };
};
